import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import DeviceForm, { DeviceFormData, deviceFormInitialValues } from 'components/forms/device/DeviceForm';
import { DEVICE_REDIRECT_AFTER_SAVE, FEATURE_TOGGLE_DEVICE_QR_CODES } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DEVICES_PAGE_PATH, EDIT_DEVICE_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to create a device.
 *
 */
export default function AddDevicePage() {
  const navigate = useNavigate();
  const { getAction } = useApi();
  const [searchParams] = useSearchParams();

  const [initialValues] = useState(() => ({
    ...deviceFormInitialValues,
    departmentId: searchParams.get('departmentId') ?? '',
    deviceTypeId: searchParams.get('deviceTypeId') ?? '',
  }));

  /**
   * Runs when the form is submitted.
   */
  function onSubmit({
    deviceName,
    manufacturer,
    serialNumber,
    qrId,
    manufactured,
    internalPossessionsNumber,
    internalNote,
    building,
    floor,
    room,
    longitude,
    latitude,
    deviceTypeId,
    deviceSubtypeId,
    departmentId,
    responsiblePersonId,
    responsiblePersonNote,
    nextRevision,
    files,
    evidenceFields,
    structure = 'simple',
    parentDeviceId,
    childDeviceIds,
  }: DeviceFormData) {
    const deviceCreateAction = getAction('DeviceCreate');
    const qrPayload = FEATURE_TOGGLE_DEVICE_QR_CODES && qrId !== '' ? { qrId } : {};

    const mappedFiles = files.map((val) => ({
      fileName: val.fileName,
      fileId: val.fileId,
      fileType: val.type,
      isMainImage: val.isMainImage,
    }));

    const mappedRevisions = nextRevision
      .filter(({ description, revisionPeriod }) => Number(revisionPeriod) !== 0 && description !== '')
      .filter(({ deleted }) => !deleted)
      .map((revision) => {
        let date: string | undefined = '';

        if (revision.neverExecuted) {
          date = revision.nextDate !== '' ? revision.nextDate! : undefined;
        } else {
          date = revision.date !== '' ? revision.nextDate! : undefined;
        }

        return {
          description: revision.description,
          date,
          revisionPeriod: Number(revision.revisionPeriod),
          neverExecuted: revision.neverExecuted,
          deviceTypeId: revision.deviceTypeId ? Number(revision.deviceTypeId) : undefined,
        };
      });

    return deviceCreateAction({
      payload: {
        deviceName,
        manufacturer: manufacturer === '' ? undefined : manufacturer,
        serialNumber: serialNumber === '' ? undefined : serialNumber,
        ...qrPayload,
        manufactured: String(manufactured) === '' ? undefined : manufactured,
        internalPossessionsNumber,
        internalNote,
        building,
        floor,
        room,
        longitude,
        latitude,
        deviceTypeId: Number(deviceTypeId),
        deviceSubtypeId: Number(deviceSubtypeId),
        departmentId: Number(departmentId),
        responsiblePersonId: responsiblePersonId ? Number(responsiblePersonId) : undefined,
        responsiblePersonNote,
        nextRevision: mappedRevisions,
        files: mappedFiles,
        evidenceFields: Object.values(evidenceFields),
        structure,
        parentDeviceId: parentDeviceId ? Number(parentDeviceId) : undefined,
        childDeviceIds: childDeviceIds ? childDeviceIds.map(Number) : undefined,
      },
    })
      .then((response) => {
        notifications.show({
          title: 'Zariadenie bolo vytvorené',
          message: `Zariadenie ${deviceName} bol úspešne vytvorený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (DEVICE_REDIRECT_AFTER_SAVE === 'same') {
          navigate(EDIT_DEVICE_PAGE_PATH.insert({ deviceId: response.deviceId }), { replace: true });
        } else if (DEVICE_REDIRECT_AFTER_SAVE === 'list') {
          navigate(DEVICES_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  return (
    <DashboardLayout
      title="Pridanie nového zariadenia"
      breadcrumbs={[{ title: 'Zariadenia', link: DEVICES_PAGE_PATH.original }, { title: 'Pridanie nového zariadenia' }]}
    >
      <DeviceForm
        onSubmit={onSubmit}
        initialValues={initialValues}
        readOnly={{ faults: true }}
        isAddMode
        primaryButtonText="Vytvoriť"
        primaryIcon={<IconPlus stroke="1.5" height={24} width={24} />}
      />
    </DashboardLayout>
  );
}
